/**
 * PRENUMERATIONEN — vägen in till att betala, och vägen ut.
 *
 * DEN HÄR SKÄRMEN ÄR APPENS ENDA KASSA. Utan den går varje firma in i sju
 * gratisdagar, låses av nattsvepet på dag åtta och möter texten "inget nytt kan
 * sparas förrän betalningen är ordnad" utan att det finns någon väg att ordna
 * den. BILLING_CHECKOUT anropas härifrån och ingen annanstans.
 *
 * SÖKVÄGEN ÄR INTE FRI. Servern skickar tillbaka kunden från Stripe till
 * /prenumeration (worker/src/modules/billing/subscription.ts rad 126-127 och
 * 148). Byts raden i app/skarmar.ts landar en betalande kund på startsidan
 * utan besked.
 *
 * TRE TAL, OCH INGET AV DEM SKRIVS FÖR HAND
 *   ANTALET     current_billed_user_count ur svaret — antal UPPLAGDA med en
 *               betald roll JUST NU (beslut 18), räknat av servern med exakt
 *               samma fråga som stängningen. Räknar klienten själv visar
 *               skärmen ett annat tal än fakturan.
 *   PRISET      price_per_user_ore ur samma svar. PRICE_PER_USER_ORE finns i
 *               kontraktet, men det är SERVERNS tal som debiteras.
 *   DAGEN       subscription.current_period_end. Det ÄR firmans ankare — både
 *               nästa dragning och gränsen mellan två perioder. Firman som
 *               signade den 20:e betalar den 27:e, varje månad. Aldrig den 1:a.
 *
 * TECKNA-KNAPPEN VISAS I TVÅ LÄGEN, 'trialing' och 'locked', och det är
 * serverns regel och inte en smaksak: BILLING_CHECKOUT svarar ROW_DUPLICATE på
 * en LÖPANDE prenumeration (subscription.ts rad 112-118), eftersom en andra
 * checkout hade gett firman en andra cykel och ett andra ankare. En knapp som
 * bara kan svara nej ska inte stå där.
 *
 * OP_ID FRYSES VID FÖRSTA TRYCKET (beslut 09), ett per knapp. Ett omtag med
 * samma op_id spelas upp ur account_op_log och ger SAMMA Stripe-adress i
 * stället för en andra session. Det släpps — och myntas om — bara när servern
 * SVARAT och avvisat; efter ett nätfel vet vi inte om anropet gick fram, och då
 * ligger det kvar.
 *
 * BOKFÖRAREN NÅR SKÄRMEN. Han står i FULL_VIEW_ROLES och ska kunna stämma av
 * Klartids perioder mot bokföringen — därför listan längst ner, med serverns
 * egna tal. Knapparna kräver ÄGAREN (beslut 14, requires_owner), så för honom
 * och för en manager som inte äger firman finns de inte.
 */

import { useEffect, useState } from 'react';
import { PAID_ROLES } from '@delat/types';
import type { Millis, OpId, SubscriptionStatus } from '@delat/types';
import {
  Bricka,
  Felruta,
  Installningsrad,
  Knapp,
  Laddar,
  Listrad,
  Rubrik,
  Tomtext,
  Yta,
} from '../ui';
import { atgardFor } from '../api';
import type { Klartidfel } from '../api';
import { tillArbetsdag, visaDagMedAr, visaOren, visaPeriod } from '../app/datum';
import { useInloggad } from '../app/session';
import { PRENUMERATIONSNAMN, ROLLNAMN } from './oversikt';
import { visaPersoner } from './LagetData';
import {
  gaTillBetalningen,
  glomNyssBetalat,
  lasBetalningsflagga,
  markeraNyssBetalat,
  nollaBetalningsflagga,
  nyssBetalat,
  nyttOpId,
  oppnaBetalningssidan,
  tecknaPrenumerationen,
  usePrenumerationen,
} from './prenumeration-data';
import type { Betalningsbesked } from './prenumeration-data';

/**
 * Vad datumet BETYDER i varje läge. Talet är detsamma — ankaret — men "nästa
 * dragning" vore en lögn i provperioden och i låsläget.
 */
const ANKARETIKETT: Readonly<Record<SubscriptionStatus, string>> = {
  trialing: 'Provperioden tar slut',
  active: 'Nästa dragning',
  past_due: 'Nästa dragning',
  locked: 'Perioden tog slut',
};

/** ['chef', 'montör', 'lärling'] -> 'chef, montör och lärling'. */
const ochLista = (delar: readonly string[]): string =>
  delar.length < 2
    ? (delar[0] ?? '')
    : `${delar.slice(0, -1).join(', ')} och ${delar[delar.length - 1] ?? ''}`;

/** Rollerna som kostar, lästa ur kontraktet och aldrig uppräknade för hand. */
const BETALDA_ROLLER = ochLista(PAID_ROLES.map((roll) => ROLLNAMN[roll]));

/** Datum med år: visaDag ger "Torsdag 27 augusti", och året kan vara ett annat. */
const medAr = (tid: Millis): string => visaDagMedAr(tillArbetsdag(tid));

/** Brickan bredvid läget. Bara de två lägen som kräver något av chefen bär en. */
const LAGESBRICKA: Readonly<Record<SubscriptionStatus, string | null>> = {
  trialing: null,
  active: null,
  past_due: 'obetald',
  locked: 'pausad',
};

/* --- Beskedet efter Stripe --------------------------------------------- */

const Betalningsruta = ({
  besked,
  onUppdatera,
}: {
  readonly besked: Betalningsbesked;
  readonly onUppdatera: () => void;
}) => (
  <Yta tyst rubrik={besked === 'klar' ? 'Tack — betalningen är mottagen' : 'Du avbröt i kassan'}>
    <p style={{ margin: 0 }}>
      {besked === 'klar'
        ? // WEBHOOKEN KAN VARA PÅ VÄG. Stripe bekräftar mot servern i en egen
          // begäran, och den kan komma efter att telefonen redan är tillbaka.
          // Texten får därför ALDRIG påstå att något gått fel om läget nedanför
          // fortfarande säger provperiod — betalningen ÄR gjord, det är bara
          // beskedet som är på väg.
          'Det kan ta en minut innan det syns här nedanför. Står det kvar på provperiod en stund till — hämta läget igen, betalningen är gjord ändå.'
        : 'Ingenting har dragits och ingenting har ändrats. Knappen står kvar tills du vill.'}
    </p>
    {besked === 'klar' && (
      <div style={{ marginTop: '0.7rem' }}>
        <Knapp variant="piller" onClick={onUppdatera}>
          Hämta läget igen
        </Knapp>
      </div>
    )}
  </Yta>
);

/* --- Skärmen ------------------------------------------------------------ */

export const Prenumeration = () => {
  const { firmaId, aktiv, arAgare } = useInloggad();
  const [omgang, sattOmgang] = useState(0);
  const lage = usePrenumerationen(firmaId, omgang);

  // Flaggan läses EN gång, medan den finns kvar i adressen, och nollas sedan i
  // en effekt. Läses den i effekten i stället hinner StrictMode köra den två
  // gånger och det andra varvet ser en tom adress.
  const [besked] = useState<Betalningsbesked | null>(lasBetalningsflagga);
  useEffect(() => {
    // STÄMPELN SÄTTS INNAN FLAGGAN FÖRSVINNER. Den är det enda som överlever en
    // omladdning, och utan den kunde en pull-to-refresh i webhookfönstret ge en
    // ANDRA prenumeration hos Stripe — se prenumeration-data.ts.
    if (besked === 'klar') markeraNyssBetalat(firmaId);
    if (besked === 'avbruten') glomNyssBetalat(firmaId);
    nollaBetalningsflagga();
  }, [besked, firmaId]);

  const [tecknaOp, sattTecknaOp] = useState<OpId | null>(null);
  const [portalOp, sattPortalOp] = useState<OpId | null>(null);
  const [skickar, sattSkickar] = useState<'teckna' | 'avsluta' | null>(null);
  const [fel, sattFel] = useState<Klartidfel | null>(null);

  const tryck = (vilken: 'teckna' | 'avsluta'): void => {
    const frusen = vilken === 'teckna' ? tecknaOp : portalOp;
    const op = frusen ?? nyttOpId();
    if (frusen === null) {
      if (vilken === 'teckna') sattTecknaOp(op);
      else sattPortalOp(op);
    }

    sattSkickar(vilken);
    sattFel(null);
    void (async () => {
      const svar =
        vilken === 'teckna'
          ? await tecknaPrenumerationen(firmaId, op)
          : await oppnaBetalningssidan(firmaId, op);
      // Alltid av igen, oavsett utfall: en knapp som fastnar i "Öppnar ..."
      // efter ett tappat nät är en app man startar om.
      sattSkickar(null);
      if (!svar.ok) {
        sattFel(svar.fel);
        // SERVERN HAR SVARAT OCH SAGT NEJ -> ingen session blev till, och nästa
        // tryck får mynta ett nytt op_id. Vid ett NÄTFEL ligger det frusna
        // kvar: då vet vi inte om anropet gick fram, och samma op_id ger samma
        // adress i stället för en andra session hos Stripe.
        if (svar.fel.slag === 'api' && atgardFor(svar.fel) === 'give_up') {
          if (vilken === 'teckna') sattTecknaOp(null);
          else sattPortalOp(null);
        }
        return;
      }
      gaTillBetalningen(svar.varde);
    })();
  };

  if (lage.status === 'laddar') {
    return (
      <>
        <Rubrik titel={aktiv.name} under="Vad Klartid kostar firman, och när det dras." />
        {besked !== null && (
          <Betalningsruta besked={besked} onUppdatera={() => sattOmgang((n) => n + 1)} />
        )}
        <Laddar rader={4} />
      </>
    );
  }

  if (lage.status === 'fel') {
    return (
      <>
        <Rubrik titel={aktiv.name} under="Vad Klartid kostar firman, och när det dras." />
        {besked !== null && (
          <Betalningsruta besked={besked} onUppdatera={() => sattOmgang((n) => n + 1)} />
        )}
        <Felruta
          fel={lage.fel}
          atgard={
            <Knapp variant="piller" onClick={() => sattOmgang((n) => n + 1)}>
              Försök igen
            </Knapp>
          }
        />
      </>
    );
  }

  const { prenumeration, perioder } = lage.vy;
  const rad = prenumeration.subscription;
  const antal = prenumeration.current_billed_user_count;
  const pris = Number(prenumeration.price_per_user_ore);
  const summa = antal * pris;
  const last = rad.status === 'locked';
  /**
   * TECKNA-KNAPPEN, och den försvinner i tre lägen.
   * 'active' och 'past_due' — servern svarar ROW_DUPLICATE (se filhuvudet).
   * OCH SÅ LÄNGE EN KASSA NYSS GICK IGENOM: då kan webhooken vara på väg, och
   * läget här står ännu på 'trialing' med stripe_subscription_id = null. Ett
   * andra tryck i det fönstret hade skapat en ANDRA prenumeration hos Stripe —
   * utan att någon kod stoppade det, för raden ser oteckad ut. Knappen "Hämta
   * läget igen" i rutan ovanför är vägen vidare i stället.
   *
   * SPÄRREN LIGGER I TELEFONEN OCH INTE I KOMPONENTENS MINNE. Låg den i minnet
   * släppte en omladdning — pull-to-refresh, precis det texten uppmanar till —
   * fram knappen igen. Se prenumeration-data.ts.
   */
  const fardigKassa = besked === 'klar' || nyssBetalat(firmaId);
  const kanTeckna = (rad.status === 'trialing' || last) && !fardigKassa;

  // Nyast först: den period som stängdes sist är den man letar efter.
  const stangda =
    perioder === null
      ? null
      : perioder.slice().sort((a, b) => Number(b.period_start) - Number(a.period_start));

  /**
   * HAR FIRMAN NÅGONSIN BETALAT? SubscriptionPublicRow skalar bort
   * stripe_subscription_id (beslut 07), så en firma som låstes efter en obetald
   * faktura och en som bara lät provperioden gå ut ser identiska ut i 'locked'.
   * Signalen finns ändå: en stängd period betyder att en dragning har skett.
   * Utan den här kontrollen fick en firma som ALDRIG tecknat en knapp
   * "Avsluta prenumerationen" som öppnade en tom betalningssida hos Stripe.
   *
   * `perioder === null` betyder att BILLING_PERIODS föll — då vet vi inte, och
   * då är det bättre att visa vägen till betalningssidan än att gömma den.
   */
  const harBetalat = stangda === null || stangda.length > 0;

  /**
   * BETALNINGSSIDAN HAR TVÅ ÄRENDEN: där avslutas prenumerationen OCH där byts
   * kortet. Stripe har bara en sida, och BILLING_PORTAL är enda vägen dit. I
   * 'past_due' är det KORTBYTET ägaren är på väg att göra — en knapp som då bara
   * säger "Avsluta prenumerationen" ber honom trycka på motsatsen till det han
   * vill. Samma sträng står i texten ovanför knappen, ordagrant.
   */
  const portalknapp =
    rad.status === 'past_due' ? 'Byt kort eller avsluta' : 'Avsluta prenumerationen';

  return (
    <>
      <Rubrik titel={aktiv.name} under="Vad Klartid kostar firman, och när det dras." />

      {besked !== null && (
        <Betalningsruta besked={besked} onUppdatera={() => sattOmgang((n) => n + 1)} />
      )}

      <Yta rubrik="Läget">
        <Installningsrad
          etikett="Prenumeration"
          varde={PRENUMERATIONSNAMN[rad.status]}
          atgard={
            LAGESBRICKA[rad.status] === null ? undefined : (
              <Bricka ton="pagaende">{LAGESBRICKA[rad.status]}</Bricka>
            )
          }
        />
        <Installningsrad
          etikett={ANKARETIKETT[rad.status]}
          varde={medAr(rad.current_period_end)}
        />
      </Yta>

      {/* ANKARET, med ord. Beslut 18: dagen firman signade är dess betalningsdag
          för all framtid, och den går inte att byta för en löpande
          prenumeration — bara en omteckning efter ett lås ger ett nytt. */}
      <Tomtext>
        Firman har sin egen betalningsdag: samma datum varje månad, räknat från
        den dag ni skrev upp er. Aldrig den 1:a, och den går inte att flytta.
      </Tomtext>

      <Yta rubrik="Vad det kostar">
        {/* VARJE BELOPP BÄR SIN MOMSMÄRKNING DÄR DET STÅR, också multiplikanden
            — den som räknar i huvudet ska inte behöva leta efter enheten i en
            annan rad. */}
        <Listrad
          titel={`${visaPersoner(antal)} med betald roll`}
          bi={`× ${visaOren(pris)}/mån ex moms`}
          varde={`${visaOren(summa)} ex moms`}
        />
        <Tomtext>
          Betald roll är {BETALDA_ROLLER}. Varje person som är UPPLAGD i firman med en
          av dem kostar, oavsett om han rapporterat en enda timme den månaden. Inhyrda
          och bokförare kostar ingenting, och ingen tid räknas av.
        </Tomtext>
      </Yta>

      {fel !== null && <Felruta fel={fel} />}

      {/* --- Teckna. Bara ägaren, och bara i de två lägen servern tillåter. --- */}
      {arAgare && kanTeckna && (
        <Knapp disabled={skickar !== null} onClick={() => tryck('teckna')}>
          {skickar === 'teckna'
            ? 'Öppnar kassan ...'
            : last
              ? 'Teckna igen'
              : tecknaOp === null
                ? 'Teckna Klartid'
                : 'Försök igen'}
        </Knapp>
      )}

      {arAgare && kanTeckna && last && (
        <Tomtext>
          Betalningen dras i dag, och den dagen blir firmans nya betalningsdag varje
          månad. Allt ni redan lagt in ligger kvar och går att skriva i igen direkt.
        </Tomtext>
      )}

      {arAgare && kanTeckna && !last && (
        <Tomtext>
          Kortet sparas för kommande dragningar. Provperioden räknas av först — du
          betalar ingenting förrän dagen ovanför.
        </Tomtext>
      )}

      {/* OBETALD FAKTURA. Kortet byts på SAMMA sida som avslutar — Stripe har
          bara en, och BILLING_PORTAL är enda vägen dit. Meningen om kortet står
          därför bara för ägaren, som är den enda som når knappen. Se LUCKOR:
          knappen förtjänar ett andra namn i det här läget. */}
      {rad.status === 'past_due' && (
        <Tomtext>
          Den senaste betalningen gick inte igenom. Vi försöker igen, men ordnas det
          inte pausas firman — och då går inget nytt att spara.
          {arAgare
            ? ` Har kortet gått ut byter du det längst ner, på "${portalknapp}".`
            : ''}
        </Tomtext>
      )}

      {rad.status === 'active' && (
        <Tomtext>
          Prenumerationen löper. Nästa dragning står ovanför, och beloppet följer hur
          många som är upplagda den dagen.
        </Tomtext>
      )}

      {!arAgare && (
        <Tomtext>
          Bara firmans ägare tecknar och avslutar prenumerationen. Du ser läget,
          beloppet och perioderna.
        </Tomtext>
      )}

      {/* --- Historiken. Bokförarens avstämning mot bokföringen. ------------ */}
      <Yta
        rubrik="Betalda perioder"
        rakning={stangda === null ? undefined : `${stangda.length} st`}
      >
        {stangda === null ? (
          <Tomtext>
            Perioderna gick inte att hämta just nu. Läget ovanför stämmer ändå.
          </Tomtext>
        ) : stangda.length === 0 ? (
          <Tomtext>
            Ingen period är stängd än. Den första stängs på firmans betalningsdag, och
            då hamnar den här.
          </Tomtext>
        ) : (
          stangda.map((period) => (
            <Listrad
              key={String(period.period_start)}
              titel={visaPeriod(period.period_start, period.period_end)}
              under={`${visaPersoner(period.billed_user_count)} med betald roll`}
              varde={`${visaOren(period.amount_ore)} ex moms`}
            />
          ))
        )}
      </Yta>

      {/* --- Vägen ut. Tyst, längst ner, och med sanningen ovanför. ---------
          INTE I PROVPERIODEN. Där finns ingen prenumeration hos Stripe att
          avsluta — CREATE_COMPANY har lagt upp en KUND, inte ett abonnemang —
          och en knapp som öppnar en tom betalningssida är sämre än ingen knapp.
          Den som inte vill fortsätta låter helt enkelt bli att teckna.
          INTE HELLER FÖR EN LÅST FIRMA SOM ALDRIG TECKNAT: provperiodssvepet
          (worker/src/modules/billing/trials.ts) låser den utan att någon
          prenumeration någonsin skapats hos Stripe, och "inte provperiod" räckte
          därför inte som villkor. Se `harBetalat` ovan. */}
      {arAgare && rad.status !== 'trialing' && harBetalat && (
        <>
          {/* VAD SOM HÄNDER MED PENGARNA OCH NÄR — de två frågor den som
              funderar på att säga upp faktiskt har. Kontraktet fakturerar i
              FÖRSKOTT en klumpsumma per ankarperiod och proportionerar aldrig
              (schema/d1.sql: "aldrig en halv period att proportionera"), så en
              uppsägning dagen efter dragningen har betalt för en hel månad. */}
          <Tomtext>
            Du har betalt till och med {medAr(rad.current_period_end)}. Säger du upp
            gäller prenumerationen perioden ut och pausas sedan — perioden
            proportioneras inte och betalas inte tillbaka. Dina timmar ligger kvar:
            du kan läsa och exportera allt även efter det, det som stängs är att
            spara nytt.
          </Tomtext>
          {/* KNAPPEN LÄMNAR APPEN. Trycket avslutar ingenting själv — det
              skickar hela telefonen till Stripes portal, som ser annorlunda ut
              och delvis är på engelska. Det ska sägas innan, inte upptäckas. */}
          <Tomtext>
            Du skickas till vår betalningsleverantör Stripe. Där trycker du
            {rad.status === 'past_due'
              ? ' Update payment method eller Cancel plan'
              : ' Cancel plan'}
            {' '}— och kommer tillbaka hit när du är klar.
          </Tomtext>
          <Knapp
            variant="stor-tyst"
            disabled={skickar !== null}
            onClick={() => tryck('avsluta')}
          >
            {skickar === 'avsluta'
              ? 'Öppnar betalningssidan ...'
              : portalOp === null
                ? portalknapp
                : 'Försök igen'}
          </Knapp>
        </>
      )}

      {/* LÅST UTAN ATT NÅGONSIN HA BETALAT. Ingen knapp — det finns ingenting
          att avsluta — men firman ska få veta det, inte gissa. */}
      {arAgare && rad.status !== 'trialing' && !harBetalat && (
        <Tomtext>
          Firman pausades när provperioden tog slut, så det finns ingen
          prenumeration att avsluta — ingenting har dragits. Tecknar du igen
          startar den på dagens datum, och allt ni lagt in ligger kvar.
        </Tomtext>
      )}

      {arAgare && rad.status === 'trialing' && (
        <Tomtext>
          Provperioden blir aldrig en faktura av sig själv. Tecknar du inte pausas
          firman när dagarna är slut — och allt du lagt in ligger kvar att läsa och
          exportera.
        </Tomtext>
      )}
    </>
  );
};
